import {
  capitalizeFirstLetterOfEachWord,
  convertToSnakeCase,
} from 'module/helpers/string-utils';
import { useTranslation } from 'module/hooks/use-translation';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { Loading } from 'module/ui/components/atoms/loading';
import { Layout } from 'module/ui/components/templates/argon/components';
import {
  faChevronDown,
  faChevronUp,
  faGlobe,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faLightbulbOn,
  faTriangleExclamation,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons';
import { useParams } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import {
  useGetAccountSignalsSidebarItemsQuery,
  useGetFilteredSignalsQuery,
} from 'queries/all-signals';
import { memo, useCallback, useMemo, useState } from 'react';
import { EmptyState } from 'components/atoms/empty-state';
import { SignalOutputCard } from 'components/organisms/signal-output';
import { useFilterState } from 'components/pages/all-signals/shared/use-filter-state';
import {
  AllSignalsSidebar,
  SidebarSearchWrapper,
  AllSignalsContentWrapper,
  SectionHeader,
  Count,
  SectionSubItem,
  SectionSubItemLabel,
  AllSignalsSidebarMenu,
  AllSignalsAccountHeaderWrapper,
  SignalsContentWrapper,
  ExpandIcon,
  SignalsContainer,
} from './styles';
import { AccountHeader } from '../common/components';
import type { MenuProps } from 'antd';

export enum SignalConfigurationType {
  RISK = 'RISK',
  OPPORTUNITY = 'OPPORTUNITY',
  OPERATIONAL = 'OPERATIONAL',
  EVERYTHING = 'EVERYTHING',
}

const iconMap = {
  RISK: faTriangleExclamation,
  OPPORTUNITY: faLightbulbOn,
  OPERATIONAL: faWrench,
  EVERYTHING: faGlobe,
};

export const AllSignalsContent = ({
  apiFilter,
  groupedFields,
}: {
  apiFilter: GroupedFilter | null;
  groupedFields: GroupedOptions<FilterableField>[];
}) => {
  const searchPlaceholder = useTranslation('search');
  const { accountId: companyId } = useParams();
  const [value, setValue] = useState('');
  const [current, setCurrent] = useState<number>(0);

  const { filter, selectedSignals, updateFilter, updateSelectedSignals } =
    useFilterState(
      apiFilter ?? defaultGroupedFilter(FilterGroupType.Company),
      [],
      { selectedSignals: [] },
    );

  const {
    data: accountSignalsSidebarItems,
    isLoading: isSidebarItemsLoading,
    isSuccess,
  } = useGetAccountSignalsSidebarItemsQuery(Number(companyId));

  const { data: filteredSignals, isLoading: isFilteredSignalsLoading } =
    useGetFilteredSignalsQuery(
      {
        filter: filter,
        signalConfigurationId: current,
        searchValue: value,
        startRow: 0,
        endRow: 100,
        companyId: Number(companyId),
      },
      { enabled: isSuccess || !!current },
    );

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const clear = useCallback(() => {
    setValue('');
  }, []);

  const sidebarItems = useMemo(() => {
    if (isSidebarItemsLoading) {
      return [];
    }

    let draft: MenuProps['items'] = [];

    Object.entries(accountSignalsSidebarItems ?? {}).forEach(([key, value]) => {
      draft.push({
        label: (
          <SectionHeader onClick={() => {}}>
            {capitalizeFirstLetterOfEachWord(key.toLowerCase())}
            <span style={{ flex: 1 }} />
          </SectionHeader>
        ),
        key: convertToSnakeCase(key.toLowerCase()),
        icon: <FontAwesomeIcon icon={iconMap[key]} size={16} />,
        children:
          value.map((item) => ({
            label: (
              <SectionSubItem>
                <SectionSubItemLabel>
                  {item.signalConfigurationName}{' '}
                </SectionSubItemLabel>
                <Count>{item.signalOccurrenceCount}</Count>
              </SectionSubItem>
            ),
            // key: convertToSnakeCase(item.signalConfigurationName.toLowerCase()),
            key: item.signalConfigurationId,
          })) ?? [],
      });
    });

    draft.unshift({
      label: (
        <SectionHeader onClick={() => {}}>
          {capitalizeFirstLetterOfEachWord(
            SignalConfigurationType.EVERYTHING.toLowerCase(),
          )}
          <span style={{ flex: 1 }} />
        </SectionHeader>
      ),
      key: '0',
      icon: (
        <FontAwesomeIcon
          icon={iconMap[SignalConfigurationType.EVERYTHING]}
          size={16}
        />
      ),
      // children: [],
    });

    return draft;
  }, [accountSignalsSidebarItems, isSidebarItemsLoading]);

  const handleSelect = useCallback((key: number) => {
    // setCurrent(convertToSnakeCase(key) as SignalConfigurationType);
    setCurrent(key);
  }, []);

  const SideBar = useMemo(() => {
    if (isSidebarItemsLoading) {
      return <Loading />;
    }

    if (Object.keys(accountSignalsSidebarItems ?? {}).length === 0) {
      return <EmptyState title="No signals found" />;
    }

    return (
      <>
        <SidebarSearchWrapper>
          <SearchInputExpanded
            placeholder={searchPlaceholder}
            value={value}
            onChange={onChange}
            onClear={clear}
          />
        </SidebarSearchWrapper>
        <div>
          {sidebarItems.length === 0 && value.length > 0 ? (
            <EmptyState
              title="No meetings found"
              primaryAction={
                <Button variant="secondary" onClick={clear}>
                  Clear
                </Button>
              }
              size="medium"
              centerVertically
            />
          ) : (
            <AllSignalsSidebarMenu
              items={sidebarItems}
              onSelect={({ key }) => {
                return handleSelect(Number(key));
              }}
              selectedKeys={[String(current)]}
              defaultOpenKeys={['RISK']}
              expandIcon={({ isOpen }) => (
                <ExpandIcon>
                  <FontAwesomeIcon
                    icon={isOpen ? faChevronUp : faChevronDown}
                    size={12}
                  />
                </ExpandIcon>
              )}
              mode="inline"
              inlineCollapsed={true}
            />
          )}
        </div>
      </>
    );
  }, [
    searchPlaceholder,
    value,
    onChange,
    clear,
    sidebarItems,
    current,
    handleSelect,
    accountSignalsSidebarItems,
    isSidebarItemsLoading,
  ]);

  const onFilterChange = useCallback(
    (filter: GroupedFilter) => {
      updateFilter(filter);
    },
    [updateFilter],
  );

  const onDone = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  const onCancel = useCallback(() => {
    updateFilter(apiFilter ?? defaultGroupedFilter(FilterGroupType.Company));
  }, [apiFilter, updateFilter]);

  const SignalsContent = memo(() => {
    if (isFilteredSignalsLoading) {
      return <Loading />;
    }
    if (!filteredSignals || filteredSignals?.length === 0) {
      return <EmptyState title="No signals have been detected" />;
    }

    return (
      <SignalsContainer>
        {filteredSignals?.map((signal) => {
          const reason = signal?.fields?.find(
            (field) =>
              Object.keys(signal?.metaData)[0] === String(field?.fieldId),
          );

          return (
            <SignalOutputCard
              key={signal?.signalOccurrenceId}
              sourceId={signal?.sourceId}
              sourceType={signal?.sourceType}
              people={signal?.reportedBy ?? []}
              reason={reason}
              title={signal?.title}
              description={signal?.summary}
              tasks={[]}
              insightfulContent={''}
              citations={signal?.citationMapping}
              type={signal?.signalName}
              signalType={signal?.signalType}
              className="timeline-details-signal"
            />
          );
        })}
      </SignalsContainer>
    );
  });

  SignalsContent.displayName = 'SignalsContent';

  return (
    <Layout>
      <AllSignalsSidebar>{SideBar}</AllSignalsSidebar>
      <AllSignalsContentWrapper>
        <AllSignalsAccountHeaderWrapper>
          <AccountHeader
            groupedFields={groupedFields}
            filter={filter}
            onChange={onFilterChange}
            onDone={onDone}
            onCancel={onCancel}
            selectedSignals={selectedSignals}
            setSelectedSignals={updateSelectedSignals}
            isAccountPage={true}
          />
        </AllSignalsAccountHeaderWrapper>
        <SignalsContentWrapper>
          <SignalsContent />
        </SignalsContentWrapper>
      </AllSignalsContentWrapper>
    </Layout>
  );
};
